import { guideStep, type GuideAccess, type GuideDefinition } from './guide-types'
import { hasWorkspacePermission } from '../../lib/access-control'

const pageTitle = 'main h1'
const pageContent = 'main'

export function createGuideCatalog(access: GuideAccess): GuideDefinition[] {
  const has = (permission: string) => hasWorkspacePermission(access, permission)

  const guides: GuideDefinition[] = [
    pageGuide('dashboard', '/', 'Dashboard', 'Your role-specific starting point for daily work.'),
    pageGuide('weekly-tasks', '/tasks', 'Weekly Tasks', 'Plan the week, update progress, and review staff delivery.'),
    pageGuide('leave', '/leave', 'Leave', 'Create and review leave requests available to your role.'),
    pageGuide('resources', '/documents', 'Resources', 'Store, organize, preview, and search authorized documents.'),
    pageGuide('ai', '/ai', 'WINBOX AI', 'Ask permission-aware questions about WorkHub records.'),
    pageGuide('notifications', '/notifications', 'Notifications', 'Review updates that need your attention.'),
  ]

  if (has('employees.read')) guides.push(pageGuide('employees', '/employees', 'Employees', 'Review staff records available to your role.'))
  if (has('roles.manage')) guides.push(pageGuide('roles', '/roles', 'Roles & Access', 'Manage role permissions and individual access exceptions.'))
  if (has('clients.read')) {
    guides.push(pageGuide('crm', '/crm', 'CRM & Clients', 'Work with organizations, contacts, and follow-ups.'))
    guides.push(pageGuide('opportunities', '/crm/opportunities', 'Opportunities', 'Track active business opportunities and next actions.'))
    guides.push(pageGuide('proposals', '/crm/proposals', 'Proposals', 'Review proposal versions, status, and linked opportunities.'))
  }
  if (has('programmes.read')) guides.push(pageGuide('programmes', '/programmes', 'Programmes', 'Manage programme offerings, editions, and participants.'))
  if (has('eap.read')) guides.push(pageGuide('eap', '/eap', 'EAP', 'Work inside the protected EAP coordination area.'))
  if (has('reports.read')) guides.push(pageGuide('reports', '/reports', 'Reports', 'Review and export authorized operational reports.'))
  if (has('finance.read') || has('finance.manage')) guides.push(pageGuide('invoices', '/invoices', 'Invoices', 'Review billing, balances, due dates, and payments.'))
  if (has('tools.use')) {
    guides.push(pageGuide('tools', '/tools', 'Office Tools', 'Open shared productivity tools available to employees.'))
    guides.push(pageGuide('qr', '/qr-studio', 'QR Studio', 'Generate and verify QR codes before using them.'))
    guides.push(pageGuide('converter', '/tools/document-converter', 'Document Converter', 'Convert Word, spreadsheet, text, and image files to PDF.'))
    guides.push(pageGuide('certificates', '/tools/certificates', 'Certificate Generator', 'Generate branded certificates from templates and recipient lists.'))
    guides.push(pageGuide('signature', '/tools/signature-stamp', 'Signature & Stamp', 'Place a signature or stamp directly on a document preview.'))
  }

  return guides
}

function pageGuide(id: string, path: string, title: string, description: string): GuideDefinition {
  return {
    id,
    path,
    title,
    description,
    steps: [
      guideStep('overview', pageTitle, title, description),
      guideStep(
        'workspace',
        pageContent,
        'Work in this section',
        'Actions and records on this page are already filtered by your WorkHub role and permissions.',
      ),
      guideStep(
        'guides',
        '[data-tour="workhub-guides"]',
        'Come back to Guides anytime',
        'You can replay this walkthrough or open another section guide from here.',
      ),
    ],
  }
}

const detailRoutes = [
  { pattern: /^\/employees\/[^/]+$/, parent: 'employees', title: 'Employee profile' },
  { pattern: /^\/crm\/organizations\/[^/]+$/, parent: 'crm', title: 'Organization record' },
  { pattern: /^\/crm\/contacts\/[^/]+$/, parent: 'crm', title: 'Contact record' },
  { pattern: /^\/crm\/proposals\/[^/]+$/, parent: 'proposals', title: 'Proposal details' },
  { pattern: /^\/programmes\/editions\/[^/]+$/, parent: 'programmes', title: 'Programme edition' },
  { pattern: /^\/tasks\/[^/]+$/, parent: 'weekly-tasks', title: 'Task details' },
  { pattern: /^\/eap\/cases\/[^/]+$/, parent: 'eap', title: 'EAP case details' },
  { pattern: /^\/invoices\/[^/]+$/, parent: 'invoices', title: 'Invoice details' },
]

export function findPageGuide(catalog: GuideDefinition[], pathname: string): GuideDefinition | undefined {
  const path = pathname === '/' ? pathname : pathname.replace(/\/$/, '')
  const exact = catalog.find((guide) => guide.path === path)
  if (exact) return exact

  const detail = detailRoutes.find((route) => route.pattern.test(path))
  if (!detail || !catalog.some((guide) => guide.id === detail.parent)) return undefined

  return pageGuide(`${detail.parent}-detail`, path, detail.title, 'Review this record and the actions available to your role.')
}
